import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from './router';
import { TenantProvider } from './contexts/TenantContext';
import { supabase } from './lib/supabase';
import { useEffect } from 'react';
import SupportChatbot from './components/SupportChatbot';
import { useLocation } from 'react-router-dom';

// BUILD MODE CHECK
const isBuildMode = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const hasBuildParam = urlParams.get('build') === '1';
  const hasEnvFlag = import.meta.env.DEV && import.meta.env.VITE_DEV_BUILD_MODE === 'true';
  return hasBuildParam || hasEnvFlag;
};

// Component to conditionally render chatbot based on route
function ConditionalChatbot() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  
  return isDashboard ? <SupportChatbot /> : null;
}

function AppContent() {
  const showBuildBanner = isBuildMode();

  // ✅ CRITICAL: Global auth state listener for cross-tab sync
  useEffect(() => {
    console.log('🔐 [App] Setting up global auth state listener...');
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 [App] Auth State Change:', event, session ? 'Session active' : 'No session');
      
      // ✅ This ensures all tabs stay in sync with auth state
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        console.log('✅ [App] User authenticated in this tab');
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 [App] User signed out in this tab');
      }
    });

    // ✅ Listen for visibility changes to refresh session (but only once per visibility change)
    let isCheckingVisibility = false;
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && !isCheckingVisibility) {
        isCheckingVisibility = true;
        console.log('👁️ [App] Tab became visible - checking session...');
        
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            console.log('✅ [App] Session still active');
          } else {
            console.log('⚠️ [App] No session found');
          }
        } catch (err) {
          console.error('❌ [App] Session check failed:', err);
        } finally {
          // Reset flag after a short delay to prevent rapid re-checks
          setTimeout(() => {
            isCheckingVisibility = false;
          }, 1000);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <TenantProvider>
      {/* Build Mode Banner */}
      {showBuildBanner && (
        <div className="bg-yellow-400 text-yellow-900 px-4 py-2 text-center text-sm font-medium">
          🔨 BUILD MODE AKTIV - Nur für Entwicklung (Keine echten Daten)
        </div>
      )}

      <AppRoutes />
      
      {/* Support Chatbot - Only in Dashboard */}
      <ConditionalChatbot />
    </TenantProvider>
  );
}

function App() {
  return (
    <BrowserRouter basename={__BASE_PATH__}>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
